import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AvatarPipe } from 'app/core/pipes/avatar.pipe';
import { AuthService } from 'app/modules/auth/auth.service';
import { NgApexchartsModule } from 'ng-apexcharts';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';

@Component({
  selector: 'app-staff-dashboard',
  standalone: true,
  imports: [NgClass, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, AvatarPipe, GlobalFilterPanelComponent, NgApexchartsModule],
  templateUrl: './staff-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class StaffDashboardComponent {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  readonly dashboardType = 'staff';
  filters: any = {};

  mode: 'user' | 'module' = 'user';

  rosterData: { users: any[]; module_totals: Record<string, number> } = { users: [], module_totals: {} };
  loadingRoster = false;

  userDetail: any = null;
  moduleDetail: any = null;
  loadingDetail = false;
  detailError: string | null = null;

  selectedUserId: number | null = null;
  selectedModule: string | null = null;
  cameFromModule: string | null = null;

  // ── Overview charts (empty-state landing panel) ──────────────────────────
  userTrendChartOpts: any = null;
  modulePendingChartOpts: any = null;
  loadingCharts = false;

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
    status: false,
    priority: false,
    compareTo: true,
  };

  readonly STAFF_MODULES = [
    { key: 'procurement', label: 'Procurement', icon: 'engineering' },
    { key: 'happiness', label: 'Happiness Center', icon: 'sentiment_satisfied' },
    { key: 'tasks', label: 'Tasks', icon: 'task_alt' },
    { key: 'services', label: 'Services', icon: 'manage_accounts' },
    { key: 'inspections', label: 'Inspections', icon: 'search' },
    { key: 'accounting', label: 'Accounting Bills', icon: 'receipt_long' },
  ].filter((m) => this.isModuleVisible(m.key));

  /**
   * Inspections and Call Center are activated per community; hide the module when no community of the company has it on.
   * The 'happiness' module counts call center tickets (backend table call_center_tickets).
   */
  isModuleVisible(key: string): boolean {
    const company = this.auth.profile()?.company;
    if (key === 'inspections') return !!company?.has_inspections;
    if (key === 'happiness') return !!company?.has_call_center;
    return true;
  }

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadRoster();
    this.loadCharts();
    if (this.selectedUserId) this.loadUserDetail(this.selectedUserId);
    else if (this.selectedModule) this.loadModuleDetail(this.selectedModule);
    this.cdr.markForCheck();
  }

  setMode(m: 'user' | 'module'): void {
    this.mode = m;
    this.userDetail = null;
    this.moduleDetail = null;
    this.selectedUserId = null;
    this.selectedModule = null;
    this.cameFromModule = null;
    this.cdr.markForCheck();
  }

  selectUser(userId: number, fromModule: string | null = null): void {
    this.selectedUserId = userId;
    this.selectedModule = null;
    this.cameFromModule = fromModule;
    this.mode = 'user';
    this.loadUserDetail(userId);
  }

  selectModule(key: string): void {
    this.selectedModule = key;
    this.selectedUserId = null;
    this.cameFromModule = null;
    this.mode = 'module';
    this.loadModuleDetail(key);
  }

  backToModule(): void {
    if (!this.cameFromModule) return;
    this.selectModule(this.cameFromModule);
  }

  getModuleLabel(key: string): string {
    return this.STAFF_MODULES.find((m) => m.key === key)?.label ?? key;
  }

  /** Mirrors each module's `pending_statuses` in DashboardStaffPerformanceService::MODULES / ACCOUNTING. */
  getPendingStatusTooltip(key: string | null): string {
    const map: Record<string, string> = {
      procurement: 'Work orders with status Active or Completion Request Sent.',
      happiness: 'Happiness Center tickets with status Open, In-Process, or Hold.',
      tasks: 'Tasks with status Pending or In Progress.',
      services: 'Applications with status Pending, Onhold, Approved, Security Clear, Wait for Documents, Waiting for Payment, Payment Clear, or Rescheduled.',
      accounting: 'Bills with status New, Sent for Review, Sent for Approval, Resubmitted, Sent for Audit Approval, Sent Back, Audit Returned, Returned, or Hold.',
    };
    return (key && map[key]) ?? 'Items currently pending action for this module.';
  }

  /** Mirrors each module's `done_statuses` in DashboardStaffPerformanceService::MODULES / ACCOUNTING —
   *  counted when their update date falls within the selected date range. */
  getDoneStatusTooltip(key: string | null): string {
    const map: Record<string, string> = {
      procurement: 'Work orders with status Completed, updated within the selected date range.',
      happiness: 'Happiness Center tickets with status Closed, updated within the selected date range.',
      tasks: 'Tasks with status Completed or Closed, updated within the selected date range.',
      services: 'Applications with status Completed, Declined, Processed, Cancelled, Refund, Notified, NOC Issued, Terminated, or Expired, updated within the selected date range.',
      accounting: 'Bills with status Paid or Approved, updated within the selected date range.',
    };
    return (key && map[key]) ?? 'Items marked done for this module, updated within the selected date range.';
  }

  /** Company-wide pending total for a module — comes straight from the backend so
   *  it correctly includes items assigned to external/blocked users. */
  getModuleTotal(key: string): number {
    return this.rosterData.module_totals?.[key] ?? 0;
  }

  getPendingBadgeClass(n: number): string {
    if (n === 0) return 'bg-gray-100 text-gray-400';
    if (n <= 4) return 'bg-green-100 text-green-700';
    if (n <= 20) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  }

  getAgingClass(days: number): string {
    if (days >= 15) return 'text-red-600 font-semibold';
    if (days >= 7) return 'text-amber-600 font-semibold';
    return 'text-gray-400';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  getModuleColor(key: string): string {
    const map: Record<string, string> = {
      procurement: '#3b82f6',
      happiness: '#10b981',
      tasks: '#8b5cf6',
      services: '#f59e0b',
      inspections: '#ef4444',
      accounting: '#6366f1',
    };
    return map[key] ?? '#6b7280';
  }

  getModuleIcon(key: string): string {
    return this.STAFF_MODULES.find((m) => m.key === key)?.icon ?? 'work';
  }

  /** Percentage of total aging that falls in a given bucket. */
  getAgingPct(aging: any, bucket: string): number {
    if (!aging) return 0;
    const total = (aging.b0 ?? 0) + (aging.b1 ?? 0) + (aging.b2 ?? 0) + (aging.b3 ?? 0) + (aging.b4 ?? 0);
    if (total === 0) return 0;
    return Math.round(((aging[bucket] ?? 0) / total) * 100);
  }

  /** Dynamic title for the user trend chart based on the active period filter */
  get trendChartTitle(): string {
    const from = this.filters?.from_date;
    const to = this.filters?.to_date;
    if (!from && !to) return 'User Status Trend — All Time';
    if (from && to) {
      const f = new Date(from + 'T00:00:00');
      const t = new Date(to + 'T00:00:00');
      const fmtShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      return `User Status Trend — ${fmtShort(f)} to ${fmtShort(t)}`;
    }
    return 'User Status Trend';
  }

  // ─── Compare-to helpers ──────────────────────────────────────────────────

  /** True when the current filters include a comparison period */
  get hasCompare(): boolean {
    return !!this.filters?.compare_from_date;
  }

  /** Absolute percentage change between current and compare values */
  getDeltaPct(current: number, compare: number | null | undefined): number {
    if (compare == null) return 0;
    if (compare === 0) return current > 0 ? 100 : 0;
    return Math.abs(Math.round(((current - compare) / compare) * 100));
  }

  /** Direction of change */
  getDeltaDir(current: number, compare: number | null | undefined): 'up' | 'down' | 'same' {
    if (compare == null) return 'same';
    if (current > compare) return 'up';
    if (current < compare) return 'down';
    return 'same';
  }

  /** Material icon name for the direction */
  getDeltaIcon(current: number, compare: number | null | undefined): string {
    const dir = this.getDeltaDir(current, compare);
    if (dir === 'up') return 'arrow_upward';
    if (dir === 'down') return 'arrow_downward';
    return 'remove';
  }

  /**
   * CSS text + background classes for the delta badge pill.
   * higherIsBetter=true  → up=green, down=red   (completions, active days)
   * higherIsBetter=false → down=green, up=red   (pending load, oldest age)
   */
  getDeltaBadgeClass(current: number, compare: number | null | undefined, higherIsBetter = true): string {
    const dir = this.getDeltaDir(current, compare);
    if (dir === 'same') return 'bg-gray-100 text-gray-500';
    const isGood = (dir === 'up') === higherIsBetter;
    return isGood ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700';
  }

  // ─── Data loaders ────────────────────────────────────────────────────────

  private loadRoster(): void {
    this.loadingRoster = true;
    this.engine
      .getWidgetData(this.dashboardType, 'staffRoster', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const data = res.record?.data ?? res.record ?? {};
          this.rosterData = Array.isArray(data) ? { users: data, module_totals: {} } : { users: data.users ?? [], module_totals: data.module_totals ?? {} };
          this.loadingRoster = false;
          // Build module bar chart from the same module_totals the sidebar uses
          // so the numbers always match and the active filters are already applied.
          this.buildModuleChart();
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingRoster = false;
          this.cdr.markForCheck();
        },
      });
  }

  private buildModuleChart(): void {
    const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#6366f1'];
    const labels = this.STAFF_MODULES.map((m) => m.label);
    const pending = this.STAFF_MODULES.map((m) => this.rosterData.module_totals?.[m.key] ?? 0);
    const maxVal = Math.max(...pending, 1);

    this.modulePendingChartOpts = {
      series: [{ name: 'Pending', data: pending }],
      chart: { type: 'bar', height: 280, toolbar: { show: false }, fontFamily: 'inherit' },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '50%',
          distributed: true,
          dataLabels: { position: 'top' },
        },
      },
      colors,
      legend: { show: false },
      dataLabels: {
        enabled: true,
        offsetY: -22,
        style: { fontSize: '12px', fontWeight: 700, colors: ['#374151'] },
        formatter: (val: number) => (val === 0 ? '' : val),
      },
      xaxis: { categories: labels, labels: { style: { fontSize: '11px' } } },
      yaxis: {
        min: 0,
        max: Math.ceil(maxVal * 1.25),
        labels: { style: { fontSize: '11px' } },
        title: { text: 'Pending items', style: { fontSize: '11px', fontWeight: 500 } },
      },
      tooltip: { theme: 'light' },
      grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
    };
  }

  private loadUserDetail(userId: number): void {
    this.loadingDetail = true;
    this.detailError = null;
    this.userDetail = null;
    this.engine
      .getWidgetData(this.dashboardType, 'staffUserDetail', { ...this.filters, user_id: userId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.userDetail = res.record?.data ?? res.record ?? null;
          this.loadingDetail = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.detailError = err.status === 403 ? 'Access restricted' : 'Unable to load user detail';
          this.loadingDetail = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadModuleDetail(module: string): void {
    this.loadingDetail = true;
    this.detailError = null;
    this.moduleDetail = null;
    this.engine
      .getWidgetData(this.dashboardType, 'staffModuleDetail', { ...this.filters, module })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.moduleDetail = res.record?.data ?? res.record ?? null;
          this.loadingDetail = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.detailError = err.status === 403 ? 'Access restricted' : 'Unable to load module detail';
          this.loadingDetail = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadCharts(): void {
    this.loadingCharts = true;

    // ── User trend line chart ────────────────────────────────────────────────
    this.engine
      .getWidgetData(this.dashboardType, 'staffUserTrend', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data ?? res.record ?? {};
          const months: string[] = d.months ?? [];
          // Format labels: '2025-06' → 'Jun 25'
          const labels = months.map((m: string) => {
            const [y, mo] = m.split('-');
            return new Date(+y, +mo - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          });
          this.userTrendChartOpts = {
            series: [
              { name: 'Active', data: d.active ?? [] },
              { name: 'Blocked', data: d.blocked ?? [] },
              { name: 'Archived', data: d.archived ?? [] },
            ],
            chart: { type: 'line', height: 260, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
            stroke: { curve: 'smooth', width: 2 },
            colors: ['#10b981', '#f59e0b', '#6b7280'],
            xaxis: { categories: labels, labels: { style: { fontSize: '11px' } } },
            yaxis: { min: 0, labels: { style: { fontSize: '11px' } }, title: { text: 'Users', style: { fontSize: '11px', fontWeight: 500 } } },
            legend: { position: 'top', fontSize: '12px' },
            tooltip: { theme: 'light' },
            grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
            markers: { size: 3 },
          };
          this.loadingCharts = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingCharts = false;
          this.cdr.markForCheck();
        },
      });
  }
}
